import React, { useState } from 'react';
import { 
  PieChart, 
  Plus, 
  Calendar, 
  Users, 
  Clock, 
  CheckCircle2, 
  AlertCircle,
  Loader2
} from 'lucide-react';
import { 
  Card, 
  Button, 
  Badge 
} from '../components/ui';
import { useTenant } from '../contexts/TenantContext';
import { usePermissions } from '../hooks/usePermissions';
import { toast } from 'react-hot-toast';
import { Modal } from '../components/ui/Modal';
import { Input } from '../components/ui/Input';
import { useElections, useCandidates, useVote, useCreateElection } from '../hooks/useElections';
import { cn } from '../lib/utils';
import { apiWorker } from '../api/worker';
import { Election } from '../types';
import { useQueryClient } from '@tanstack/react-query';

interface PollCardProps {
  election: Election;
  onVoteCast?: () => void;
}

const PollCard: React.FC<PollCardProps> = ({ election, onVoteCast }) => {
  const { data: candidates = [], isLoading: isLoadingCandidates } = useCandidates(election.id);
  const voteMutation = useVote(election.id);
  const [selectedCandidateId, setSelectedCandidateId] = useState<string>('');
  const [hasVotedLocally, setHasVotedLocally] = useState<boolean>(() => {
    try {
      return localStorage.getItem(`voted_poll_${election.id}`) === 'true';
    } catch {
      return false;
    }
  });

  const totalCandidateVotes = candidates.reduce((sum, c) => sum + (c.votesReceived || 0), 0);
  const totalVotes = Math.max(totalCandidateVotes, (election as any).totalVotes || 0);
  const isActive = election.status === 'ACTIVE';

  const handleVote = () => {
    if (!selectedCandidateId) {
      toast.error('Please select an option first');
      return;
    }

    voteMutation.mutate(selectedCandidateId, {
      onSuccess: () => {
        setHasVotedLocally(true);
        try {
          localStorage.setItem(`voted_poll_${election.id}`, 'true');
        } catch {
          // Best-effort: private-mode storage must not block the vote.
        }
        onVoteCast?.();
      }
    });
  };

  const isVoted = hasVotedLocally || voteMutation.isSuccess;

  return (
    <Card className="overflow-hidden rounded-none border-2 border-black dark:border-white">
      <div className="flex flex-col md:flex-row">
        <div className="flex-1 p-6">
          <div className="flex items-center gap-2 mb-2">
            <Badge variant={isActive ? 'green' : 'gray'}>
              {election.status}
            </Badge>
            {isVoted && (
              <Badge variant="blue" className="gap-1">
                <CheckCircle2 size={10} /> VOTED
              </Badge>
            )}
          </div>
          <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">{election.title}</h3>
          {election.description && (
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-6">{election.description}</p>
          )}

          {isLoadingCandidates ? (
            <div className="flex items-center justify-center py-8 text-gray-400">
              <Loader2 className="animate-spin mr-2" size={18} />
              <span className="text-xs uppercase font-mono tracking-widest">Loading options...</span>
            </div>
          ) : candidates.length === 0 ? (
            <div className="p-4 border border-dashed border-black/20 dark:border-white/20 text-center text-xs text-gray-500 font-mono">
              No options configured for this poll.
            </div>
          ) : (
            <div className="space-y-4">
              {candidates.map((cand) => {
                const votes = cand.votesReceived || 0;
                const percentage = totalVotes > 0 ? Math.round((votes / totalVotes) * 100) : 0;
                const isSelected = selectedCandidateId === cand.id;
                const optionLabel = cand.name || cand.statement || cand.role || 'Option';

                return (
                  <div 
                    key={cand.id} 
                    className={cn(
                      "relative p-3 rounded-none border-2 transition-all",
                      isActive && !isVoted && "cursor-pointer hover:border-brand-primary/50",
                      isSelected ? "border-brand-primary bg-brand-primary/5" : "border-black/10 dark:border-white/10"
                    )}
                    onClick={() => {
                      if (isActive && !isVoted) {
                        setSelectedCandidateId(cand.id);
                      }
                    }}
                  >
                    <div className="flex justify-between items-center mb-1.5 text-sm">
                      <span className={cn("font-medium", isSelected ? "font-bold text-brand-primary" : "text-gray-700 dark:text-gray-200")}>
                        {optionLabel}
                      </span>
                      <span className="text-gray-500 font-mono text-xs">{votes} votes ({percentage}%)</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-none overflow-hidden dark:bg-slate-800">
                      <div 
                        className={`h-full rounded-none transition-all duration-500 ${isActive ? 'bg-brand-primary' : 'bg-gray-400'}`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        
        <div className="md:w-64 bg-white p-6 border-t md:border-t-0 md:border-l border-black dark:border-white flex flex-col justify-between dark:bg-slate-950">
          <div className="space-y-4">
            <div className="flex items-start gap-2 text-xs text-gray-500">
              <Clock size={14} className="shrink-0 mt-0.5" />
              <span>
                {isActive 
                  ? (election.endDate ? `Ends on ${new Date(election.endDate).toLocaleDateString()}` : 'Active Ongoing') 
                  : (election.endDate ? `Closed on ${new Date(election.endDate).toLocaleDateString()}` : 'Concluded')}
              </span>
            </div>
            <div className="flex items-start gap-2 text-xs text-gray-500">
              <Users size={14} className="shrink-0 mt-0.5" />
              <span>{totalVotes} total participants</span>
            </div>
          </div>
          
          <div className="mt-6">
            {isActive ? (
              isVoted ? (
                <Button variant="ghost" disabled className="w-full text-green-600 bg-green-50 border-green-100 rounded-none">
                  <CheckCircle2 size={16} className="mr-1.5" /> Vote Recorded
                </Button>
              ) : (
                <Button 
                  variant="primary" 
                  className="w-full shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] rounded-none" 
                  onClick={handleVote}
                  disabled={voteMutation.isPending || !selectedCandidateId}
                >
                  {voteMutation.isPending ? 'Casting...' : 'Cast Vote'}
                </Button>
              )
            ) : (
              <Button variant="secondary" className="w-full rounded-none" disabled>
                Poll Closed
              </Button>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};

export const PollsPage = () => {
  const { node, getTerm } = useTenant();
  const { can } = usePermissions();
  const queryClient = useQueryClient();

  const { data: elections = [], isLoading: isLoadingElections } = useElections(node?.id || '', 'POLL');
  
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newPollQuestion, setNewPollQuestion] = useState('');
  const [newPollDescription, setNewPollDescription] = useState('');
  const [newPollOptions, setNewPollOptions] = useState<string[]>(['Option 1', 'Option 2']);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const createPollMutation = useCreateElection(node?.id || '');

  const handleAddOption = () => {
    setNewPollOptions(prev => [...prev, `Option ${prev.length + 1}`]);
  };

  const handleOptionChange = (index: number, val: string) => {
    setNewPollOptions(prev => prev.map((opt, idx) => idx === index ? val : opt));
  };

  const handleRemoveOption = (index: number) => {
    if (newPollOptions.length <= 2) return;
    setNewPollOptions(prev => prev.filter((_, idx) => idx !== index));
  };

  const handleCreatePoll = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPollQuestion.trim()) return;

    const validOptions = newPollOptions.map(opt => opt.trim()).filter(Boolean);
    if (validOptions.length < 2) {
      toast.error('Please provide at least 2 options');
      return;
    }

    setIsSubmitting(true);
    createPollMutation.mutate({
      title: newPollQuestion.trim(),
      description: newPollDescription.trim(),
      type: 'POLL',
      status: 'ACTIVE',
      startDate: new Date().toISOString(),
      endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
      options: validOptions,
      roles: validOptions,
      isAnonymous: true,
      allowSelfNomination: false
    }, {
      onSuccess: async (created: any) => {
        if (created?.id) {
          try {
            await Promise.all(
              validOptions.map(opt => 
                apiWorker.post(`/api/elections/${created.id}/candidates`, {
                  candidateId: opt,
                  name: opt,
                  role: 'Option',
                  statement: opt
                }).catch(() => null)
              )
            );
          } catch {
            // Candidate creation is best-effort; still refresh the cache below.
          }
          queryClient.invalidateQueries({ queryKey: ['candidates', created.id] });
        }
        queryClient.invalidateQueries({ queryKey: ['elections', node?.id] });
        setIsCreateModalOpen(false);
        setNewPollQuestion('');
        setNewPollDescription('');
        setNewPollOptions(['Option 1', 'Option 2']);
        setIsSubmitting(false);
      },
      onError: () => {
        setIsSubmitting(false);
      }
    });
  };

  const activePollsCount = elections.filter(p => p.status === 'ACTIVE').length;
  const totalVotesCount = elections.reduce((sum, p) => sum + ((p as any).totalVotes || 0), 0);

  const canManagePolls = can('manage_polls' as any) || can('manage_elections' as any) || can('edit_organization' as any);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black font-display text-gray-900 dark:text-white uppercase tracking-tight">Polls</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Track community sentiment and collect verified votes.</p>
        </div>
        {canManagePolls && (
          <Button 
            className="gap-2 shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] rounded-none" 
            onClick={() => setIsCreateModalOpen(true)}
          >
            <Plus size={18} /> New Poll
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-4 flex items-center gap-4 rounded-none border-2 border-black dark:border-white">
          <div className="p-3 bg-blue-50 text-blue-600 rounded-none border border-black/10">
            <PieChart size={24} />
          </div>
          <div>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest font-mono">Active Polls</p>
            <p className="text-2xl font-black text-gray-900 dark:text-white">{activePollsCount}</p>
          </div>
        </Card>
        <Card className="p-4 flex items-center gap-4 rounded-none border-2 border-black dark:border-white">
          <div className="p-3 bg-green-50 text-green-600 rounded-none border border-black/10">
            <Users size={24} />
          </div>
          <div>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest font-mono">Total Polls</p>
            <p className="text-2xl font-black text-gray-900 dark:text-white">{elections.length}</p>
          </div>
        </Card>
        <Card className="p-4 flex items-center gap-4 rounded-none border-2 border-black dark:border-white">
          <div className="p-3 bg-amber-50 text-amber-600 rounded-none border border-black/10">
            <CheckCircle2 size={24} />
          </div>
          <div>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest font-mono">Recorded Votes</p>
            <p className="text-2xl font-black text-gray-900 dark:text-white">{totalVotesCount}</p>
          </div>
        </Card>
      </div>

      {isLoadingElections ? (
        <div className="flex flex-col items-center justify-center py-20 bg-white border-2 border-black dark:border-white dark:bg-slate-900">
          <Loader2 className="animate-spin text-brand-primary mb-3" size={32} />
          <p className="text-sm font-mono uppercase tracking-widest text-gray-500">Loading node polls & elections...</p>
        </div>
      ) : elections.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-none border-2 border-black dark:border-white border-dashed dark:bg-slate-900">
          <div className="w-16 h-16 bg-white text-gray-300 rounded-none flex items-center justify-center mx-auto mb-4 border border-black/10 dark:bg-slate-950">
            <PieChart size={32} />
          </div>
          <h3 className="text-lg font-bold text-gray-900 dark:text-white">No active polls found</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-xs mx-auto">
            Create your first poll to gather direct feedback from your {getTerm('member')}s.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6">
          {elections.map((election) => (
            <PollCard 
              key={election.id} 
              election={election} 
              onVoteCast={() => queryClient.invalidateQueries({ queryKey: ['elections', node?.id] })}
            />
          ))}
        </div>
      )}

      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => !isSubmitting && setIsCreateModalOpen(false)}
        title="Create New Poll"
      >
        <form onSubmit={handleCreatePoll} className="space-y-4">
          <Input 
            label="Question"
            value={newPollQuestion}
            onChange={(e) => setNewPollQuestion(e.target.value)}
            required
            placeholder="e.g. Which project should we focus on next?"
          />
          <Input 
            label="Description (Optional)"
            value={newPollDescription}
            onChange={(e) => setNewPollDescription(e.target.value)}
            placeholder="Provide any additional context for voters."
          />
          
          <div className="space-y-2">
            <label className="text-xs font-bold text-gray-400 uppercase tracking-widest block font-mono">Poll Options</label>
            {newPollOptions.map((opt, idx) => (
              <div key={idx} className="flex gap-2">
                <Input 
                  value={opt} 
                  onChange={(e) => handleOptionChange(idx, e.target.value)}
                  placeholder={`Option ${idx + 1}`}
                  required
                />
                {newPollOptions.length > 2 && (
                  <Button 
                    variant="ghost" 
                    type="button" 
                    onClick={() => handleRemoveOption(idx)} 
                    className="px-3 text-red-500 rounded-none"
                  >
                    ✕
                  </Button>
                )}
              </div>
            ))}
            <Button 
              variant="secondary" 
              type="button" 
              size="sm" 
              onClick={handleAddOption} 
              className="mt-2 text-xs rounded-none"
            >
              + Add Another Option
            </Button>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-black dark:border-white">
            <Button 
              variant="ghost" 
              type="button" 
              onClick={() => setIsCreateModalOpen(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button 
              variant="primary" 
              type="submit"
              disabled={isSubmitting}
              className="shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] rounded-none"
            >
              {isSubmitting ? 'Publishing...' : 'Create & Publish'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

